'use client';

import { createStore, useStore } from 'zustand';
import { useContext, createContext } from 'react';
import type { Page, PageBlock } from '@impact/types';
import { takeSnapshot, applySnapshot, type Snapshot } from '../lib/snapshot';
import { setPath } from '../lib/setPath';
import { blockRegistry } from '../registry';

const COALESCE_MS = 500;
const HISTORY_LIMIT = 50;

export interface PageEditorState {
  page: Page;
  blocks: PageBlock[];
  selectedBlockId: string | null;
  hoveredGapIndex: number | null;
  rail: { open: boolean; tab: 'page' | 'block' };
  dirty: boolean;
  saving: 'idle' | 'saving' | 'error';
  history: { past: Snapshot[]; future: Snapshot[] };
  slashMenu: { position: { x: number; y: number }; insertAt: number } | null;
  announcement: string;

  selectBlock(id: string | null): void;
  setHoveredGap(index: number | null): void;
  toggleRail(): void;
  setRailTab(tab: 'page' | 'block'): void;
  openSlashMenu(position: { x: number; y: number }, insertAt: number): void;
  closeSlashMenu(): void;

  insertBlock(at: number, block: PageBlock): void;
  insertBlocks(at: number, blocks: PageBlock[]): void;
  updateBlock(id: string, patch: Partial<PageBlock>): void;
  updateBlockPath(id: string, path: string, value: unknown): void;
  deleteBlock(id: string): void;
  duplicateBlock(id: string): void;
  moveBlock(id: string, dir: 'up' | 'down'): void;
  reorderBlocks(fromIndex: number, toIndex: number): void;
  setVariant(id: string, variantId: string, applyDefaults?: (data: unknown) => unknown): void;

  announce(msg: string): void;

  setSaving(status: 'idle' | 'saving' | 'error'): void;
  markClean(): void;
  setPage(page: Page): void;

  undo(): void;
  redo(): void;
}

interface CreateOptions {
  page: Page;
  blocks: PageBlock[];
  now?: () => number;
}

export function createPageEditorStore({ page, blocks, now = () => Date.now() }: CreateOptions) {
  let lastEditAt = 0;
  let lastEditWasText = false;
  let announceTimer: ReturnType<typeof setTimeout> | null = null;

  return createStore<PageEditorState>((set, get) => {
    const pushHistory = (coalesce = false) => {
      const t = now();
      const canCoalesce = coalesce && lastEditWasText && t - lastEditAt < COALESCE_MS;
      lastEditAt = t;
      lastEditWasText = coalesce;
      if (canCoalesce) {
        set({ dirty: true });
        return;
      }
      set((s) => {
        const past = [...s.history.past, takeSnapshot(s.blocks)];
        if (past.length > HISTORY_LIMIT) past.shift();
        return { history: { past, future: [] }, dirty: true };
      });
    };

    const findIndex = (id: string) => get().blocks.findIndex((b) => b.id === id);

    return {
      page,
      blocks,
      selectedBlockId: null,
      hoveredGapIndex: null,
      rail: { open: false, tab: 'page' },
      dirty: false,
      saving: 'idle',
      history: { past: [], future: [] },
      slashMenu: null,
      announcement: '',

      announce: (msg: string) => {
        set({ announcement: msg });
        if (announceTimer) clearTimeout(announceTimer);
        announceTimer = setTimeout(() => set({ announcement: '' }), 1500);
      },

      selectBlock: (id) =>
        set((s) => ({ selectedBlockId: id, rail: { ...s.rail, tab: id ? 'block' : 'page' } })),
      setHoveredGap: (index) => set({ hoveredGapIndex: index }),
      toggleRail: () => set((s) => ({ rail: { ...s.rail, open: !s.rail.open } })),
      setRailTab: (tab) => set((s) => ({ rail: { ...s.rail, tab } })),
      openSlashMenu: (position, insertAt) => set({ slashMenu: { position, insertAt } }),
      closeSlashMenu: () => set({ slashMenu: null }),

      insertBlock: (at, block) => {
        pushHistory(false);
        set((s) => {
          const next = [...s.blocks];
          next.splice(at, 0, block);
          return { blocks: next, selectedBlockId: block.id };
        });
        const label = blockRegistry[block.type]?.label ?? block.type;
        get().announce(`${label} inserted`);
      },

      insertBlocks: (at, newBlocks) => {
        pushHistory(false);
        set((s) => {
          const next = [...s.blocks];
          next.splice(at, 0, ...newBlocks);
          return { blocks: next, selectedBlockId: newBlocks[0]?.id ?? s.selectedBlockId };
        });
        get().announce(`${newBlocks.length} blocks inserted`);
      },

      updateBlock: (id, patch) => {
        pushHistory(true);
        set((s) => ({
          blocks: s.blocks.map((b) =>
            b.id === id
              ? ({
                  ...b,
                  ...patch,
                  data: { ...b.data, ...((patch.data ?? {}) as object) },
                } as PageBlock)
              : b,
          ),
        }));
      },

      updateBlockPath: (id, path, value) => {
        pushHistory(true);
        set((s) => ({
          blocks: s.blocks.map((b) => (b.id === id ? (setPath(b, path, value) as PageBlock) : b)),
        }));
      },

      deleteBlock: (id) => {
        const blockToDelete = get().blocks.find((b) => b.id === id);
        const label = blockToDelete
          ? (blockRegistry[blockToDelete.type]?.label ?? blockToDelete.type)
          : 'Block';
        pushHistory(false);
        set((s) => ({
          blocks: s.blocks.filter((b) => b.id !== id),
          selectedBlockId: s.selectedBlockId === id ? null : s.selectedBlockId,
        }));
        get().announce(`${label} deleted`);
      },

      duplicateBlock: (id) => {
        pushHistory(false);
        const idx = findIndex(id);
        if (idx === -1) return;
        const original = get().blocks[idx];
        const label = blockRegistry[original.type]?.label ?? original.type;
        const clone = structuredClone(original);
        clone.id = crypto.randomUUID();
        set((s) => {
          const next = [...s.blocks];
          next.splice(idx + 1, 0, clone);
          return { blocks: next, selectedBlockId: clone.id };
        });
        get().announce(`${label} duplicated`);
      },

      moveBlock: (id, dir) => {
        const idx = findIndex(id);
        const target = dir === 'up' ? idx - 1 : idx + 1;
        if (idx === -1 || target < 0 || target >= get().blocks.length) return;
        pushHistory(false);
        set((s) => {
          const next = [...s.blocks];
          [next[idx], next[target]] = [next[target], next[idx]];
          return { blocks: next };
        });
      },

      reorderBlocks: (fromIndex, toIndex) => {
        pushHistory(false);
        set((s) => {
          const next = [...s.blocks];
          const [moved] = next.splice(fromIndex, 1);
          next.splice(toIndex, 0, moved);
          return { blocks: next };
        });
      },

      setVariant: (id, variantId, applyDefaults) => {
        pushHistory(false);
        set((s) => ({
          blocks: s.blocks.map((b) => {
            if (b.id !== id) return b;
            const newData = applyDefaults ? applyDefaults(b.data) : b.data;
            return { ...b, data: { ...(newData as object), variant: variantId } } as PageBlock;
          }),
        }));
        get().announce('Style changed');
      },

      setSaving: (status) => set({ saving: status }),
      markClean: () => set({ dirty: false }),
      setPage: (next) => set({ page: next }),

      undo: () => {
        const { history, blocks } = get();
        const prev = history.past[history.past.length - 1];
        if (!prev) return;
        set({
          blocks: applySnapshot(prev),
          history: {
            past: history.past.slice(0, -1),
            future: [...history.future, takeSnapshot(blocks)],
          },
        });
      },

      redo: () => {
        const { history, blocks } = get();
        const next = history.future[history.future.length - 1];
        if (!next) return;
        set({
          blocks: applySnapshot(next),
          history: {
            past: [...history.past, takeSnapshot(blocks)],
            future: history.future.slice(0, -1),
          },
        });
      },
    };
  });
}

const StoreContext = createContext<ReturnType<typeof createPageEditorStore> | null>(null);

export const PageEditorStoreProvider = StoreContext.Provider;

export function usePageEditorStore<T>(selector: (s: PageEditorState) => T): T {
  const store = useContext(StoreContext);
  if (!store) throw new Error('usePageEditorStore must be used inside PageEditorStoreProvider');
  return useStore(store, selector);
}
